import type { Doc, Id } from "@convex/_generated/dataModel";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { Link } from "@tanstack/react-router";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { Eye, EyeOff, Info } from "lucide-react";
import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";

export interface EmployeeListItem {
  id: Id<"employees">;
  organizationId: Id<"organizations">;
  firstName: string;
  lastName: string;
  imageUrl?: string;
  email?: string;
  phoneNumber?: string;
  pin: string;
  disabled?: boolean;
  roles: Array<Doc<"organizationRoles"> | null>;
  locations: {
    isAll: boolean;
    data: Array<Doc<"locations"> | null>;
  };
}

const EmployeeRow = ({ row }: { row: Row<EmployeeListItem> }) => {
  return (
    <Link params={{ id: row.original.id }} to="/employees/$id">
      <div className="flex items-center gap-3">
        <img
          alt={`${row.getValue<string>("name")}'s avatar`}
          className="-my-0.5 size-9 rounded-full"
          height={36}
          src={row.original.imageUrl ?? "/images/avatar-placeholder.svg"}
          width={36}
        />
        {row.getValue("name")}
      </div>
    </Link>
  );
};

export const employeeTableColumns: ColumnDef<EmployeeListItem>[] = [
  {
    accessorKey: "name",
    header: "Name",
    filterFn: "includesString",
    accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    cell: ({ row }) => <EmployeeRow row={row} />,
  },
  {
    accessorKey: "pin",
    header: "Pin",
    cell: ({ row }) => {
      return <PinDisplay pin={row.getValue("pin")} />;
    },
  },
  {
    accessorKey: "phoneNumber",
    header: "Phone Number",
    accessorFn: (row) =>
      row.phoneNumber ? applyPhoneNumberMask(row.phoneNumber) : "N/A",
    cell: ({ row }) => {
      const maskedPhone = row.original.phoneNumber
        ? applyPhoneNumberMask(row.original.phoneNumber)
        : "N/A";
      return (
        <Link params={{ id: row.original.id }} to="/employees/$id">
          {maskedPhone}
        </Link>
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    accessorFn: (row) => row.email ?? "N/A",
    cell: ({ row }) => (
      <Link params={{ id: row.original.id }} to="/employees/$id">
        {row.getValue("email")}
      </Link>
    ),
  },
  {
    accessorKey: "roles",
    header: "Roles",
    accessorFn: (row) =>
      row.roles
        .map((role) => role?.name ?? "")
        .filter(Boolean)
        .join(", "),
    filterFn: (row, _, value: string[]) =>
      row.original.roles.some((role) => role && value.includes(role._id)),
    cell: ({ row }) => {
      const roles = row.getValue<string>("roles");
      if (row.original.roles.length > 0) {
        return roles;
      }
      return (
        <div className="body-sm text-foreground flex items-center gap-2">
          -
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="text-warning size-5" />
                <span className="sr-only">
                  This employee doesn&apos;t have a role
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Assign a role to this employee</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  {
    accessorKey: "locations",
    header: "Locations",
    accessorFn: (row) =>
      row.locations.isAll
        ? "All"
        : row.locations.data
            .map((location) => location?.name ?? "")
            .filter(Boolean)
            .join(", "),
    filterFn: (row, _, value: string[]) =>
      row.original.locations.data.some(
        (location) => location && value.includes(location._id)
      ),
  },
  {
    accessorKey: "status",
    header: "Status",
    accessorFn: (row) => (row.disabled ? "Disabled" : "Enabled"),
    filterFn: (row, _, value: string[]) => {
      const status = row.original.disabled ? "Disabled" : "Enabled";
      return value.includes(status);
    },
    cell: ({ row }) => {
      return (
        <div className="body-sm text-foreground flex items-center gap-2">
          <span>{row.getValue("status")}</span>
          {row.original.disabled && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="text-foreground size-5" />
                  <span className="sr-only">This employee is disabled</span>
                </TooltipTrigger>
                <TooltipContent side="left">
                  This employee is disabled and cannot log in to the POS
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    },
  },
];

export const PinDisplay = ({ pin }: { pin: string }) => {
  const [showPin, setShowPin] = useState(false);
  return (
    <div className="text-foreground flex items-center">
      <p className="w-13 tabular-nums">{showPin ? pin : "••••"}</p>
      <button
        className="-my-0.5 cursor-pointer p-2"
        onClick={(e) => {
          setShowPin((prev) => !prev);
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setShowPin((prev) => !prev);
            e.stopPropagation();
          }
        }}
        type="button"
      >
        {showPin ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
        <span className="sr-only">{showPin ? "Hide" : "Show"} pin</span>
      </button>
    </div>
  );
};
